import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth'
import { create } from 'zustand'
import { auth } from '@/lib/firebase'

export interface AuthUser {
  uid: string
  email: string
  emailVerified: boolean
  displayName: string
  photoURL: string | null
}

interface AuthState {
  status: 'loading' | 'out' | 'in'
  user: AuthUser | null
  error: string | null
}

export const useAuth = create<AuthState>()(() => ({ status: 'loading', user: null, error: null }))

function toAuthUser(u: User): AuthUser {
  return {
    uid: u.uid,
    email: (u.email ?? '').toLowerCase(),
    emailVerified: u.emailVerified,
    displayName: u.displayName ?? '',
    photoURL: u.photoURL,
  }
}

/** Starts the Firebase Auth listener. Returns the unsubscribe. */
export function startAuthListener(): () => void {
  // A redirect sign-in (the iPhone fallback) lands here on return.
  getRedirectResult(auth).catch((err: unknown) => {
    useAuth.setState({ error: describe(err) })
  })
  return onAuthStateChanged(auth, (u) => {
    useAuth.setState({ status: u ? 'in' : 'out', user: u ? toAuthUser(u) : null })
  })
}

function provider(): GoogleAuthProvider {
  const p = new GoogleAuthProvider()
  p.setCustomParameters({ prompt: 'select_account' })
  return p
}

/**
 * Google sign-in. Starts with a popup; on browsers that refuse it (the installed
 * app on iPhone can), falls back to a redirect through the app's own domain.
 */
export async function signInWithGoogle(): Promise<void> {
  useAuth.setState({ error: null })
  try {
    await signInWithPopup(auth, provider())
  } catch (err: unknown) {
    const code = (err as { code?: string }).code ?? ''
    if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
      await signInWithRedirect(auth, provider())
      return
    }
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return
    useAuth.setState({ error: describe(err) })
  }
}

export async function signOutUser(): Promise<void> {
  const uid = auth.currentUser?.uid
  if (uid) {
    const { disablePush } = await import('@/lib/push')
    await disablePush(uid)
  }
  await signOut(auth)
}

function describe(err: unknown): string {
  const e = err as { code?: string; message?: string }
  return e.code ? `Sign-in failed (${e.code}).` : e.message || 'Sign-in failed.'
}
